import React, { useId } from "react";

/**
 * MapPinArt — Phase 6/7 branded map-pin preview (SVG-only).
 *
 * Renders a Hidden Gem style teardrop pin whose gem cut, tint, and shimmer
 * scale with the reputation tier. Not the final on-map marker (that arrives
 * with Phase 5 / Mapbox), but it uses the same visual DNA so the vendor's
 * preview matches what will render on the map.
 *
 * Props:
 *   tier    — one of the 7 ladder keys (white, green, blue, purple, gold, ruby, diamond)
 *   size    — height in px (default 96)
 *   locked  — when true, dim + lock overlay
 *   selected — when true, add a soft selection ring
 */
const TIER_META = {
  white:   { primary: "#F1F5F9", accent: "#CBD5E1", facet: "rgba(255,255,255,0.35)" },
  green:   { primary: "#39FF14", accent: "#0AAE00", facet: "rgba(255,255,255,0.55)" },
  blue:    { primary: "#60A5FA", accent: "#1E40AF", facet: "rgba(255,255,255,0.55)" },
  purple:  { primary: "#FF6B35", accent: "#B45309", facet: "rgba(255,255,255,0.55)" },
  gold:    { primary: "#B5E2FF", accent: "#0891B2", facet: "rgba(255,255,255,0.7)" },
  ruby:    { primary: "#7C3AED", accent: "#4C1D95", facet: "rgba(255,255,255,0.65)" },
  diamond: { primary: "#FFD700", accent: "#B45309", facet: "rgba(255,255,255,0.85)" },
};

export default function MapPinArt({ tier = "white", size = 96, locked = false, selected = false }) {
  const meta = TIER_META[tier] || TIER_META.white;
  const uid = useId().replace(/[:]/g, "");
  return (
    <div
      className={`relative inline-grid place-items-center transition-transform ${selected ? "scale-105" : ""}`}
      style={{ width: size, height: size, opacity: locked ? 0.35 : 1 }}
      data-testid={`map-pin-art-${tier}`}
    >
      <svg viewBox="0 0 120 140" width={size} height={size} className="absolute inset-0">
        <defs>
          <radialGradient id={`glow-${uid}`} cx="50%" cy="45%" r="58%">
            <stop offset="0%" stopColor={meta.primary} stopOpacity="0.55" />
            <stop offset="70%" stopColor={meta.primary} stopOpacity="0" />
          </radialGradient>
          <linearGradient id={`body-${uid}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={meta.primary} />
            <stop offset="100%" stopColor={meta.accent} />
          </linearGradient>
          <linearGradient id={`shine-${uid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={meta.facet} />
            <stop offset="60%" stopColor="rgba(255,255,255,0)" />
          </linearGradient>
        </defs>
        {/* Ambient glow (selection amps it up) */}
        {selected && <circle cx="60" cy="55" r="56" fill={`url(#glow-${uid})`} />}
        {/* Teardrop pin body */}
        <path
          d="M60 6 C88 6 106 26 106 52 C106 82 60 132 60 132 C60 132 14 82 14 52 C14 26 32 6 60 6 Z"
          fill={`url(#body-${uid})`}
          stroke="rgba(0,0,0,0.35)"
          strokeWidth="1.5"
        />
        {/* Gem cut inside — hexagon */}
        <path
          d="M60 22 L86 40 L82 66 L60 82 L38 66 L34 40 Z"
          fill="rgba(255,255,255,0.15)"
          stroke="rgba(255,255,255,0.75)"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        {/* Facet lines */}
        <g stroke="rgba(255,255,255,0.4)" strokeWidth="0.7" fill="none">
          <path d="M60 22 L60 82" />
          <path d="M34 40 L86 40" />
          <path d="M34 40 L82 66" />
          <path d="M86 40 L38 66" />
        </g>
        {/* Top shimmer */}
        <path
          d="M60 22 L86 40 L60 52 Z"
          fill={`url(#shine-${uid})`}
        />
        {/* Selected ring */}
        {selected && (
          <path
            d="M60 6 C88 6 106 26 106 52 C106 82 60 132 60 132 C60 132 14 82 14 52 C14 26 32 6 60 6 Z"
            fill="none"
            stroke="#F472B6"
            strokeWidth="3"
            strokeDasharray="4 3"
          />
        )}
      </svg>
      {locked && (
        <svg width={size * 0.28} height={size * 0.28} viewBox="0 0 24 24" className="relative z-[1]" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0110 0v4" />
        </svg>
      )}
    </div>
  );
}
